/**
 * Create or edit a secret.
 *
 * Security rules (see docs/design/user-secret-vault-architecture.md):
 * 1. Values are only ever typed in form inputs; never rendered in read views.
 * 2. The form never echoes back previous values.
 * 3. The form stores the raw value in local state only and hands it to the
 *    API layer via HTTPS. No logging of the value anywhere.
 */

import { FormEvent, useEffect, useState } from 'react';
import { DiscardChangesDialog } from '@vanguard-atlas/shared';
import { Eye, EyeOff } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useI18n } from '@/i18n';
import {
  useCreateSecret,
  useUpdateSecret,
} from '../hooks/use-secrets';
import type { Secret, SecretType } from '../types';

// =============================================================================
// Props
// =============================================================================

interface SecretFormModalProps {
  open: boolean;
  onClose: () => void;
  /** When provided switches the modal to edit mode (metadata only). */
  secret?: Secret | null;
}

const TYPE_OPTIONS: SecretType[] = [
  'generic',
  'password',
  'api_key',
  'bearer_token',
  'basic_auth',
  'oauth2_token',
  'oauth2_client',
  'certificate',
  'ssh_key',
  'database_url',
];

// =============================================================================
// Component
// =============================================================================

export function SecretFormModal({ open, onClose, secret }: SecretFormModalProps) {
  const { t } = useI18n();
  const isEdit = !!secret;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [secretType, setSecretType] = useState<SecretType>('generic');
  const [value, setValue] = useState('');
  const [showValue, setShowValue] = useState(false);
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [dirtyBaseline, setDirtyBaseline] = useState<string | null>(null);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  const createMutation = useCreateSecret();
  const updateMutation = useUpdateSecret();

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (secret) {
      setName(secret.name);
      setDescription(secret.description ?? '');
      setSecretType((secret.secret_type as SecretType) ?? 'generic');
      setExpiresAt(secret.expires_at ? secret.expires_at.slice(0, 10) : '');
      setValue('');
      setShowValue(false);
    } else {
      setName('');
      setDescription('');
      setSecretType('generic');
      setExpiresAt('');
      setValue('');
      setShowValue(false);
    }
    // ADR-114 enmienda 1: baseline de dirty (post-init).
    setDirtyBaseline(
      JSON.stringify({
        name: secret ? secret.name : '',
        description: secret ? (secret.description ?? '') : '',
        secretType: secret ? ((secret.secret_type as SecretType) ?? 'generic') : 'generic',
        expiresAt: secret ? (secret.expires_at ? secret.expires_at.slice(0, 10) : '') : '',
        value: '',
      }),
    );
  }, [open, secret]);

  // ADR-114 enmienda 1: dirty = diff vs estado inicial.
  const isDirty =
    open &&
    dirtyBaseline !== null &&
    JSON.stringify({ name, description, secretType, expiresAt, value }) !== dirtyBaseline;
  const discardLabels = {
    title: t('common.discardChanges.title') ?? 'Discard changes?',
    message:
      t('common.discardChanges.message') ??
      'You have unsaved changes. If you close now, they will be lost.',
    confirm: t('common.discardChanges.discard') ?? 'Discard',
    cancel: t('common.discardChanges.keepEditing') ?? 'Keep editing',
  };
  const handleClose = () => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
      return;
    }
    onClose();
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError(t('admin.secrets.form.errors.nameRequired') || 'Name is required');
      return;
    }

    const expiresAtIso = expiresAt ? new Date(expiresAt).toISOString() : null;

    try {
      if (isEdit && secret) {
        await updateMutation.mutateAsync({
          id: secret.id,
          input: {
            name: trimmedName,
            description: description.trim() || null,
            secret_type: secretType,
            expires_at: expiresAtIso,
          },
        });
      } else {
        if (!value) {
          setError(t('admin.secrets.form.errors.valueRequired') || 'Value is required');
          return;
        }
        await createMutation.mutateAsync({
          name: trimmedName,
          value,
          description: description.trim() || null,
          secret_type: secretType,
          // Phase 1: secrets created from the vault are always user-scoped
          // and owned by the creator.
          scope: 'user',
          expires_at: expiresAtIso,
        });
      }
      setValue('');
      setShowValue(false);
      onClose();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t('admin.secrets.form.errors.generic') || 'Could not save secret';
      setError(message);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? handleClose() : undefined)}>
      <DialogContent
        dirty={isDirty}
        discardTitle={discardLabels.title}
        discardMessage={discardLabels.message}
        discardConfirmLabel={discardLabels.confirm}
        discardCancelLabel={discardLabels.cancel}
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t('admin.secrets.form.editTitle') || 'Edit secret'
              : t('admin.secrets.form.createTitle') || 'New secret'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? t('admin.secrets.form.editDescription') ||
                'Update metadata. To change the stored value use the rotate action.'
              : t('admin.secrets.form.createDescription') ||
                'Stored values are encrypted at rest and never shown again after creation.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="secret-name">
              {t('admin.secrets.form.name') || 'Name'}
            </Label>
            <Input
              id="secret-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              maxLength={100}
              placeholder="payment-gateway-key"
              autoComplete="off"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="secret-description">
              {t('admin.secrets.form.description') || 'Description'}
            </Label>
            <Textarea
              id="secret-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              maxLength={500}
              rows={2}
              placeholder={t('admin.secrets.form.descriptionPlaceholder') || ''}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t('admin.secrets.form.type') || 'Type'}</Label>
              <Select
                value={secretType}
                onValueChange={(next) => setSecretType(next as SecretType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`admin.secrets.types.${type}`) || type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="secret-expires">
                {t('admin.secrets.form.expiresAt') || 'Expires at'}
              </Label>
              <Input
                id="secret-expires"
                type="date"
                value={expiresAt}
                onChange={(event) => setExpiresAt(event.target.value)}
              />
            </div>
          </div>

          {!isEdit && (
            <div className="space-y-1.5">
              <Label htmlFor="secret-value">
                {t('admin.secrets.form.value') || 'Value'}
              </Label>
              <div className="flex gap-2">
                <Input
                  id="secret-value"
                  type={showValue ? 'text' : 'password'}
                  value={value}
                  onChange={(event) => setValue(event.target.value)}
                  required
                  autoComplete="off"
                  spellCheck={false}
                  className="font-mono text-sm"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowValue((prev) => !prev)}
                  aria-label={
                    showValue
                      ? t('admin.secrets.form.hideValue') || 'Hide value'
                      : t('admin.secrets.form.showValue') || 'Show value'
                  }
                >
                  {showValue ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('admin.secrets.form.valueHint') ||
                  'The value is encrypted at rest and never shown to AI models. Users with the "view" permission can reveal it later.'}
              </p>
            </div>
          )}

          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isPending}
            >
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? t('common.saving') || 'Saving…'
                : isEdit
                  ? t('common.save') || 'Save'
                  : t('admin.secrets.form.submit') || 'Create secret'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* ADR-114 enmienda 1: confirmación de descarte */}
      <DiscardChangesDialog
        open={confirmDiscardOpen}
        onDiscard={() => {
          setConfirmDiscardOpen(false);
          onClose();
        }}
        onCancel={() => setConfirmDiscardOpen(false)}
        title={discardLabels.title}
        message={discardLabels.message}
        confirmLabel={discardLabels.confirm}
        cancelLabel={discardLabels.cancel}
      />
    </Dialog>
  );
}
